import {HttpErrorResponse, HttpInterceptorFn} from '@angular/common/http';
import {inject} from '@angular/core';
import {catchError, from, switchMap, throwError} from 'rxjs';
import {NzMessageService} from 'ng-zorro-antd/message';
import {environment} from '../../../environments/environment';
import {StorageService} from '../storage.service';
import {SILENT_HTTP_ERROR} from './http-context.tokens';
import {isTerminalRefreshError, SupabaseAuthService} from '../supabase-auth.service';
import {AccountService} from '../account.service';

export const apiInterceptor: HttpInterceptorFn = (req, next) => {
  const storageService = inject(StorageService);
  const message = inject(NzMessageService);
  const supabaseAuthService = inject(SupabaseAuthService);
  const accountService = inject(AccountService);
  const silentError = req.context.get(SILENT_HTTP_ERROR);

  return from(Promise.all([
    supabaseAuthService.getAccessToken(),
    storageService.lfGet('clientId') as Promise<string | null>,
    storageService.lfGet('orgId') as Promise<string | null>,
  ])).pipe(
    switchMap(([token, clientId, orgId]) => {
      const headers: Record<string, string> = {
        'MP-App-Id': environment.appId,
      };

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      if (clientId) {
        headers['MP-Installation-Id'] = clientId;
      }

      if (orgId) {
        headers['Org-Id'] = orgId;
      }

      return next(req.clone({setHeaders: headers})).pipe(
        catchError((err: HttpErrorResponse) => {
          if (err.status !== 401 || req.headers.has('X-MP-Auth-Retry')) {
            return handleHttpError(err, silentError, message, accountService);
          }

          return from(supabaseAuthService.refreshSession()).pipe(
            catchError((refreshError: unknown) => {
              if (isTerminalRefreshError(refreshError)) {
                return logoutCurrentSession(err, silentError, message, accountService);
              }
              if (!silentError) {
                message.error('Authentication service unavailable. Please try again.');
              }
              return throwError(() => refreshError);
            }),
            switchMap((session) => {
              if (!session?.access_token) {
                return handleHttpError(err, silentError, message, accountService);
              }

              return next(req.clone({
                setHeaders: {
                  ...headers,
                  Authorization: `Bearer ${session.access_token}`,
                  'X-MP-Auth-Retry': '1',
                },
              })).pipe(
                catchError((retryErr: HttpErrorResponse) =>
                  handleHttpError(retryErr, silentError, message, accountService, false)),
              );
            }),
          );
        }),
      );
    }),
  );
};

function handleHttpError(
  err: HttpErrorResponse,
  silentError: boolean,
  message: NzMessageService,
  accountService: AccountService,
  logoutOnUnauthorized = true,
) {
  if (err.status === 401) {
    if (logoutOnUnauthorized) {
      return logoutCurrentSession(err, silentError, message, accountService);
    }
    if (!silentError) {
      message.error('Authentication failed. Please try again.');
    }
    return throwError(() => err);
  }

  if (!silentError) {
    if (err.status === 0) {
      message.error('Network Error.');
    } else if (err.status === 403) {
      message.warning('Forbidden.');
    } else if (err.status === 404) {
      message.warning(`404 ${err.message}`);
    } else if (err.status >= 500) {
      message.error('Server Error.');
    }
  }
  return throwError(() => err);
}

function logoutCurrentSession(
  err: HttpErrorResponse,
  silentError: boolean,
  message: NzMessageService,
  accountService: AccountService,
) {
  if (!silentError) {
    message.warning('Please login.');
  }
  void accountService.logout();
  return throwError(() => err);
}
